/**
 * Cómo se dibuja una fila del registro de actividad (`eventos`).
 *
 * ⚠️ **El texto de pantalla se arma acá, desde `tipo` + `detalle`, y no sale de la base.** El portal
 * y el panel leían `detalle->>'mensaje'`, y eso sólo existe en los cinco tipos que escribe la API
 * (`registrarEvento` en `portal-datos.js`). Los otros siete los escriben funciones de `auth` con un
 * `detalle` estructurado y sin ninguna frase adentro —0046, 0100, 0104, 0106, 0115, 0122, 0145—, así
 * que la fila decía literalmente **«null»**: la actividad estaba registrada y la pantalla no sabía
 * leerla. Es el mismo error en dos pantallas, y por eso el mapa vive en `comun/` y no en cada una.
 *
 * `detalle.mensaje` queda como respaldo para un tipo que este mapa no conozca todavía, y el último
 * recurso es el propio `tipo` con los guiones bajos abiertos: «Servicio suspendido» es feo pero es
 * cierto, y un tipo nuevo que nadie agregó acá se ve como lo que es —una omisión— en vez de
 * desaparecer de la lista o volver a decir «null».
 *
 * ⚠️ **Sólo se usan iconos que estén en el subconjunto de Tabler.** Uno que no esté no dibuja nada y
 * no avisa; agregarlo obliga a correr `frontend/fuentes.sh` y commitear el resultado.
 */

// Los tipos de canal, para nombrar el que se acaba de conectar. El código crudo («WEB_CHAT») no se
// muestra: es vocabulario nuestro, no del cliente.
const TIPO_CANAL = {
  whatsapp: 'WhatsApp', whatsapp_business: 'WhatsApp API', telegram: 'Telegram',
  instagram: 'Instagram', messenger: 'Messenger', web_chat: 'Chat web', email: 'Correo',
};

// Un motivo lo escribe el asistente o lo teclea una persona, así que puede ser largo: en una fila de
// registro entra el principio y nada más. El corte es por caracteres y no por palabras a propósito,
// porque lo único que importa es que la fila no se coma la pantalla.
const recorte = (t, n = 90) => {
  const s = String(t ?? '').trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

const conMotivo = (base, d) => (d?.motivo ? `${base} · ${recorte(d.motivo)}` : base);

// Los planes se guardan con su identificador (`api_oficial`), que no es un nombre para
// mostrar. Un plan que no esté acá sale con su identificador, que es feo y es cierto.
const PLANES = { esencial: 'Esencial', pro: 'Pro', api_oficial: 'API oficial' };
const nombrePlan = (n) => PLANES[n] ?? (n || 'otro plan');

// `toLocaleString` sin locale usa el del navegador: en un teléfono en inglés 60000 se dibuja
// «60,000» dentro de una frase en español. Se fija es-CL, igual que el resto del panel.
const miles = (n) => Number(n || 0).toLocaleString('es-CL');
const plata = (n, m) => `$${miles(n)} ${m || 'CLP'}`;

const EVENTOS = {
  bot_activado:          { icono: 'ti-robot',           texto: () => 'Activó el asistente de IA' },
  bot_desactivado:       { icono: 'ti-robot-off',       texto: () => 'Desactivó el asistente de IA' },
  instancia_reconectada: { icono: 'ti-plug-connected',  texto: () => 'Solicitó reconectar la línea' },
  // Desvincular NO es una caída: la línea quedó abajo porque alguien decidió cambiar el número. Sin
  // esta línea la fila sale con el tipo crudo --no falla nada, `eventos.tipo` es un varchar sin
  // CHECK-- y se confunde justo con `conexion_caida`, que es lo que hay que poder distinguir cuando
  // alguien pregunta por qué el bot estuvo mudo media hora.
  instancia_desvinculada:{ icono: 'ti-unlink',          texto: () => 'Desvinculó el número para conectar otro' },
  // `ti-key` y no `ti-password`: el subconjunto de Tabler sólo trae los iconos que el código ya
  // usaba, y uno nuevo no dibuja nada hasta correr `frontend/fuentes.sh`.
  instancia_codigo_pedido:{ icono: 'ti-key',             texto: (d) => d?.mensaje || 'Pidió el código para vincular sin escanear' },
  conexion_abierta:      { icono: 'ti-wifi',            texto: () => 'La línea quedó conectada' },
  conexion_caida:        { icono: 'ti-wifi-off',        texto: () => 'La línea se desconectó' },

  // Impago (0046). El corte es derivado, así que este evento es el único registro de cuándo pasó.
  servicio_suspendido:   { icono: 'ti-lock',            texto: () => 'Se suspendió el servicio por falta de pago' },
  servicio_restablecido: { icono: 'ti-lock-open',       texto: () => 'Se restableció el servicio' },

  // El cliente edita su propio asistente y puede deshacerlo (0100-0101): la versión es lo que se
  // nombra al revertir, así que va en el texto.
  agente_configurado: {
    icono: 'ti-adjustments',
    texto: (d) => conMotivo(d?.version ? `Guardó el asistente (versión ${d.version})`
                                       : 'Guardó el asistente', d),
  },

  // Derivación a una persona (0104/0145). El motivo lo redacta el asistente y es lo único que dice
  // de qué se trataba: sin él la fila cuenta que pasó algo y no qué.
  escalado_a_humano: {
    icono: 'ti-headset',
    texto: (d) => conMotivo('Una conversación pasó a una persona del equipo', d),
  },

  // Plan y vencimiento movidos desde el panel (0175). El cliente los ve en su registro:
  // un cambio de plan es algo que le pasó a él y enterarse por la factura es peor.
  // ⚠️ `detalle.admin_id` **no se dibuja**: quién de Deviant lo hizo es dato interno y el
  // portal ya pinta estas filas como «Sistema». Lo lee el panel, que es donde sirve.
  plan_cambiado: {
    icono: 'ti-receipt',
    texto: (d) => (d?.plan_antes
      ? `Cambió el plan: de ${nombrePlan(d.plan_antes)} a ${nombrePlan(d.plan_despues)}`
      : `Cambió el plan a ${nombrePlan(d?.plan_despues)}`),
  },
  // Plan y suscripción desde el panel (0188, 0192). ⚠️ `detalle.admin_id` **no se dibuja**:
  // quién de Deviant lo hizo es dato interno y el portal ya pinta estas filas como «Sistema».
  plan_programado: {
    icono: 'ti-calendar',
    texto: (d) => `Se agendó el cambio a ${nombrePlan(d?.plan_despues)}`
      + (d?.rige_desde ? `, desde el ${String(d.rige_desde).slice(0, 10)}` : ''),
  },
  suscripcion_creada: {
    icono: 'ti-receipt',
    texto: (d) => (d?.reactivacion
      ? `Se reactivó el servicio en ${nombrePlan(d?.plan)}`
      : `Se activó el servicio en ${nombrePlan(d?.plan)}`),
  },
  // El motivo lo escribe quien cancela y es lo único que explica la baja: va en el texto.
  suscripcion_cancelada: {
    icono: 'ti-lock',
    texto: (d) => conMotivo(`Se dio de baja la suscripción (${nombrePlan(d?.plan)})`, d),
  },
  // ⚠️ El excedente se le dice al cliente en su moneda y con los mensajes: «se cobró un
  // excedente» sin la cifra es un aviso que obliga a preguntar.
  excedente_cobrado: {
    icono: 'ti-coin',
    texto: (d) => `Se cargó el excedente de ${miles(d?.mensajes)} mensajes`
      + (d?.monto ? `: ${plata(d.monto, d.moneda)}` : ''),
  },
  excedente_condonado: {
    icono: 'ti-check',
    texto: (d) => conMotivo(`Se condonó el excedente de ${miles(d?.mensajes)} mensajes`
      + (d?.monto ? ` (${plata(d.monto, d.moneda)})` : ''), d),
  },

  vencimiento_ajustado: {
    icono: 'ti-calendar',
    texto: (d) => (d?.antes
      ? `Se ajustó la fecha de vencimiento: del ${d.antes} al ${d.despues}`
      : `Se ajustó la fecha de vencimiento al ${d?.despues}`),
  },

  contactos_unidos:      { icono: 'ti-users',           texto: () => 'Unió dos contactos en una sola ficha' },

  canal_conectado: {
    icono: 'ti-message-2',
    texto: (d) => {
      const t = TIPO_CANAL[d?.tipo_canal];
      return t ? `Se conectó un canal de ${t}` : 'Se conectó un canal nuevo';
    },
  },

  // Seguimiento del que no cerró (C.10). Encendido y apagado son la misma fila con distinto valor,
  // así que el icono también cambia: una campana tachada se lee sin leer.
  seguimiento_configurado: {
    icono: (d) => (d?.activa ? 'ti-bell' : 'ti-bell-off'),
    texto: (d) => (d?.activa
      ? `Activó el seguimiento automático${d?.espera_horas ? ` (espera ${d.espera_horas} h)` : ''}`
      : 'Desactivó el seguimiento automático'),
  },
};

/** El objeto `detalle` puede llegar como jsonb ya parseado, como texto, o no llegar. */
function comoObjeto(detalle) {
  if (detalle && typeof detalle === 'object') return detalle;
  if (typeof detalle === 'string') {
    try { return JSON.parse(detalle); } catch { return { mensaje: detalle }; }
  }
  return {};
}

export function iconoEvento(tipo, detalle) {
  const e = EVENTOS[tipo];
  if (!e) return 'ti-point';
  return typeof e.icono === 'function' ? e.icono(comoObjeto(detalle)) : e.icono;
}

export function textoEvento(tipo, detalle) {
  const d = comoObjeto(detalle);
  const e = EVENTOS[tipo];
  if (e) return e.texto(d);
  if (d.mensaje) return String(d.mensaje);
  const crudo = String(tipo ?? '').replace(/_/g, ' ');
  return crudo ? crudo.charAt(0).toUpperCase() + crudo.slice(1) : 'Actividad sin descripción';
}
